import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Mail, Phone, MapPin, MessageCircle, Download, ArrowUpRight, Network, Server, Globe, ShieldCheck, Briefcase, Award, Calendar, GraduationCap, BookOpen, HelpCircle, CheckCircle2, ArrowUp } from "lucide-react";

export default function Home() {
  return <PersonalSite />;
}


/* ============ HOOKS ============ */
function useScrollProgress(targetRef: React.RefObject<HTMLElement | null>) {
  // No React state - write the CSS var directly inside rAF.
  // Avoids a setState + re-render of the entire page on every scroll event.
  useEffect(() => {
    let ticking = false;
    const update = () => {
      ticking = false;
      const h = document.documentElement.scrollHeight - window.innerHeight;
      const p = h > 0 ? Math.min(window.scrollY / h, 1) : 0;
      const el = targetRef.current;
      if (el) el.style.setProperty("--p", String(p));
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [targetRef]);
}


function useActiveSection(ids: string[]) {
  const [active, setActive] = useState(ids[0] ?? "");
  const key = ids.join("|");
  useEffect(() => {
    const list = key.split("|");
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => e.isIntersecting && setActive(e.target.id));
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0.01 }
    );
    list.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [key]);
  return active;
}

function useReveal() {
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const items = document.querySelectorAll<HTMLElement>(".reveal");
    if (reduce) {
      items.forEach((el) => el.classList.add("in"));
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e, i) => {
          if (!e.isIntersecting) return;
          const el = e.target as HTMLElement;
          el.style.transitionDelay = `${Math.min(i * 40, 200)}ms`;
          el.classList.add("in");
          obs.unobserve(el);
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -8% 0px" }
    );
    items.forEach((el) => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      if (r.top < vh && r.bottom > 0) {
        el.classList.add("in");
      } else {
        obs.observe(el);
      }
    });
    const fallback = window.setTimeout(() => items.forEach((el) => el.classList.add("in")), 600);
    return () => {
      window.clearTimeout(fallback);
      obs.disconnect();
    };
  }, []);
}

/* ============ FAQ ACCORDION ============ */
function FaqAccordion() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const items = [
    {
      question: "What IT support does Rasel provide?",
      answer: "Desktop support, network troubleshooting, Windows Server support, DNS and hosting support, cPanel and Plesk assistance, and user guidance.",
    },
    {
      question: "Where is Rasel based?",
      answer: "Dhaka, Bangladesh - with professional experience in Dhanmondi-based IT and exam operations environments.",
    },
    {
      question: "What certifications does he hold?",
      answer: "CompTIA A+, CCNA, Windows Server 2019 / MCSA, and MTCNA - all from New Horizons CLC.",
    },
    {
      question: "Is he open to new opportunities?",
      answer: "Yes - open to IT support, network support, and technical operations roles. Get in touch via email or WhatsApp.",
    },
  ];

  return (
    <div className="faq">
      {items.map((item, i) => {
        const isOpen = openIndex === i;
        return (
          <div
            key={i}
            className={`faq-item${isOpen ? " open" : ""}`}
          >
            <button
              type="button"
              className="faq-summary"
              onClick={() => setOpenIndex(isOpen ? null : i)}
              aria-expanded={isOpen}
            >
              <HelpCircle size={18} />
              <span>{item.question}</span>
              <span className="faq-chevron" aria-hidden="true">{isOpen ? "−" : "+"}</span>
            </button>
            <div
              className="faq-answer"
              style={{
                display: "grid",
                gridTemplateRows: isOpen ? "1fr" : "0fr",
                transition: "grid-template-rows 0.3s ease",
              }}
            >
              <div style={{ overflow: "hidden" }}>
                <p>{item.answer}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}


/* ============ DATA ============ */
const NAV = [
  { id: "about", label: "About" },
  { id: "work", label: "Work" },
  { id: "stack", label: "Stack" },
  { id: "training", label: "Training" },
  { id: "faq", label: "FAQ" },
  { id: "contact", label: "Contact" },
];

const PHOTOS = [
  { src: "/assets/rasel-photo-34.jpg", label: "Executive profile" },
  { src: "/assets/rasel-photo-33.jpg", label: "Formal interview" },
  { src: "/assets/rasel-photo-11.jpg", label: "Professional casual" },
];

const MARQUEE = [
  "Network Support", "Windows Server", "Active Directory", "DNS & Hosting",
  "cPanel · Plesk", "MikroTik", "CCNA", "CompTIA A+", "Desktop Support",
];

const WORK = [
  {
    period: "Jan 2022 - Jun 2026",
    role: "IT Support Engineer",
    company: "New Horizon CLC · Dhanmondi, Dhaka",
    tag: "Full-time",
    bullets: [
      "Provided technical support for hardware, software, network and system operations.",
      "Troubleshot IT issues and assisted users with day-to-day technology problems.",
      "Supported a training environment where uptime, clarity, and quick response mattered.",
    ],
  },
  {
    period: "June 2023",
    role: "TCA Administrator",
    company: "Prometric · Dhaka",
    tag: "Part-time",
    bullets: [
      "Manages test center operations and supports secure online and offline exam delivery.",
      "Handles technical requirements for assessments with strict process compliance.",
    ],
  },
  {
    period: "May 2023",
    role: "Invigilator",
    company: "British Council · Dhaka",
    tag: "Part-time",
    bullets: [
      "Supervises examinations and helps maintain exam integrity and candidate flow.",
      "Assists exam coordination in a professional, high-accountability environment.",
    ],
  },
];

/* ============ COMPONENT ============ */
function PersonalSite() {
  const progressRef = useRef<HTMLDivElement>(null);
  useScrollProgress(progressRef);
  const active = useActiveSection(NAV.map((n) => n.id));
  useReveal();

  const [navOpen, setNavOpen] = useState(false);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [fading, setFading] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const swap = useCallback((next: number) => {
    if (next === photoIdx) return;
    setFading(true);
    window.setTimeout(() => {
      setPhotoIdx(next);
      setFading(false);
    }, 280);
  }, [photoIdx]);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    timer.current = setInterval(() => {
      setFading(true);
      window.setTimeout(() => {
        setPhotoIdx((p) => (p + 1) % PHOTOS.length);
        setFading(false);
      }, 280);
    }, 5500);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, []);

  useEffect(() => {
    if (!navOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".nav-toggle") && !target.closest(".nav-list")) {
        setNavOpen(false);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [navOpen]);

  const onNavClick = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setNavOpen(false);
  };

  const marquee = useMemo(() => [...MARQUEE, ...MARQUEE], []);

  // mouse tracking for stack cards - coalesced into one write per frame.
  const stackFrame = useRef(0);
  const onStackMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const x = e.clientX;
    const y = e.clientY;
    if (stackFrame.current) return;
    stackFrame.current = requestAnimationFrame(() => {
      stackFrame.current = 0;
      const r = el.getBoundingClientRect();
      el.style.setProperty("--mx", `${x - r.left}px`);
      el.style.setProperty("--my", `${y - r.top}px`);
    });
  }, []);

  return (
    <div className="site">
      <a href="#main" className="skip">Skip to content</a>
      <div ref={progressRef} className="progress" aria-hidden="true" />

      {/* NAV */}
      <header className="nav">
        <a href="#top" className="brand" onClick={onNavClick("top")} aria-label="Rasel Mia - home">
          <span className="brand-mark">RM</span>
          <span>Rasel Mia</span>
        </a>
        <nav aria-label="Primary">
          <div id="primary-nav" className={`nav-list${navOpen ? " open" : ""}`}>
            {NAV.map((n) => (
              <a
                key={n.id}
                href={`#${n.id}`}
                className={active === n.id ? "active" : ""}
                aria-current={active === n.id ? "page" : undefined}
                onClick={onNavClick(n.id)}
              >
                {n.label}
              </a>
            ))}
          </div>
        </nav>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <a href="#contact" className="nav-cta" onClick={onNavClick("contact")}>
            Get in touch <ArrowUpRight size={18} />
          </a>
          <button
            type="button"
            className="nav-toggle"
            aria-expanded={navOpen}
            aria-controls="primary-nav"
            aria-label={navOpen ? "Close menu" : "Open menu"}
            onClick={() => setNavOpen((v) => !v)}
          >
            <span />
          </button>
        </div>
      </header>

      <main id="main">
        {/* HERO */}
        <section id="top" className="hero">
          <div className="wrap hero-grid">
            <div>
              <span className="badge reveal"><span className="dot" />Available for IT Support roles</span>
              <h1 className="display reveal">
                Md. Rasel <span className="it">Mia.</span>
              </h1>
              <p className="hero-tag reveal">
                Dhaka-based IT Support Engineer keeping devices, networks, servers, exams and hosting environments
                running smoothly - with a calm, methodical approach.
              </p>
              <div className="hero-actions reveal">
                <a className="btn btn-primary" href="mailto:md.rasel.mia10010@gmail.com">
                  <Mail size={18} /> Email Rasel
                </a>
                <a className="btn btn-secondary" href="/assets/Md-Rasel-Mia-CV.pdf" download>
                  <Download size={18} /> Download CV
                </a>
              </div>
              <div className="hero-meta reveal" aria-label="Quick facts">
                <div>
                  <span className="meta-ico"><Briefcase size={18} /></span>
                  <div>
                    <strong>3+ yrs</strong>
                    <span>IT support</span>
                  </div>
                </div>
                <div>
                  <span className="meta-ico"><Award size={18} /></span>
                  <div>
                    <strong>4</strong>
                    <span>Certifications</span>
                  </div>
                </div>
                <div>
                  <span className="meta-ico"><MapPin size={18} /></span>
                  <div>
                    <strong>Dhaka</strong>
                    <span>Bangladesh</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="reveal">
              <div className="portrait">
                <img
                  src={PHOTOS[photoIdx].src}
                  alt="Portrait of Md. Rasel Mia"
                  className={fading ? "fading" : ""}
                  width={480}
                  height={600}
                  decoding="async"
                  fetchPriority="high"
                />
                <div className="portrait-info">
                  <div className="role">
                    <span>Currently</span>
                    <strong>IT Support Engineer</strong>
                  </div>
                  <div className="photo-dots" role="tablist" aria-label="Choose portrait">
                    {PHOTOS.map((p, i) => (
                      <button
                        key={i}
                        type="button"
                        role="tab"
                        aria-selected={i === photoIdx}
                        aria-label={`Show ${p.label}`}
                        className={i === photoIdx ? "on" : ""}
                        onClick={() => swap(i)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* MARQUEE */}
        <div className="marquee" aria-hidden="true">
          <div className="marquee-track">
            {marquee.map((m, i) => <span key={i}>{m}</span>)}
          </div>
        </div>

        {/* ABOUT */}
        <section id="about" className="section">
          <div className="wrap about">
            <div className="reveal">
              <span className="eyebrow">About</span>
              <h2 className="section-head">A practical operator, calm under pressure.</h2>
            </div>
            <div className="about-body reveal">
              <p>
                Rasel brings hands-on experience across desktop support, Cisco-style networking, MikroTik concepts,
                Windows Server 2019, Active Directory, DNS, hosting panels and customer support.
              </p>
              <p>
                The work itself is the proof: <strong>troubleshooting issues</strong>, guiding users step-by-step,
                supporting test-center technology, and following secure processes when accuracy matters most.
              </p>
              <p>
                Based in <strong>Dhaka, Bangladesh</strong>. Currently with New Horizon CLC, finishing a BSc in CSE
                at Dhaka International University.
              </p>
            </div>
          </div>
        </section>

        {/* WORK */}
        <section id="work" className="section" style={{ paddingTop: 0 }}>
          <div className="wrap">
            <div className="reveal">
              <span className="eyebrow">Work</span>
              <h2 className="section-head">Roles with real technical and operational weight.</h2>
            </div>
            <div className="work-list">
              {WORK.map((w) => (
                <article key={w.role} className="work-item reveal">
                  <div className="work-period"><Calendar size={14} />{w.period}</div>
                  <div className="work-role">
                    <h3>{w.role}</h3>
                    <p className="company">{w.company}</p>
                    <ul>
                      {w.bullets.map((b, i) => <li key={i}><CheckCircle2 size={15} />{b}</li>)}
                    </ul>
                  </div>
                  <span className="work-tag">{w.tag}</span>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* STACK */}
        <section id="stack" className="section" style={{ paddingTop: 0 }}>
          <div className="wrap">
            <div className="reveal" style={{ textAlign: "center" }}>
              <span className="eyebrow">Stack</span>
              <h2 className="section-head" style={{ marginInline: "auto" }}>Capabilities, organized for hiring clarity.</h2>
            </div>

            <div className="stack-grid">
              <article className="stack-card featured reveal" onPointerMove={onStackMove}>
                <span className="icon"><Network /></span>
                <h3>Network Support</h3>
                <p>IPv4 routing, VLAN, Inter-VLAN, NAT, PAT, ACL, VPN, firewall, DHCP, SSH, GRE tunnel and GNS3 simulation.</p>
                <div className="tags">
                  <span>Cisco</span><span>MikroTik</span><span>TP-Link</span><span>D-Link</span><span>Netgear</span>
                </div>
              </article>

              <article className="stack-card wide reveal" onPointerMove={onStackMove}>
                <span className="icon"><Server /></span>
                <h3>Systems & Desktop</h3>
                <p>Windows troubleshooting, hardware diagnostics, file shares, user access, printers, Server 2019, Active Directory, GPO and virtualization basics.</p>
                <div className="tags">
                  <span>Windows OS</span><span>AD</span><span>GPO</span><span>Backup</span>
                </div>
              </article>

              <article className="stack-card narrow reveal" onPointerMove={onStackMove}>
                <span className="icon"><Globe /></span>
                <h3>DNS, Hosting & Email</h3>
                <p>Domain setup, DNS pointing, nameservers, SSL, cPanel/Plesk access, file management, mailboxes, webmail, Outlook and Thunderbird.</p>
                <div className="tags">
                  <span>DNS</span><span>SSL</span><span>cPanel</span><span>Plesk</span>
                </div>
              </article>

              <article className="stack-card narrow reveal" onPointerMove={onStackMove}>
                <span className="icon"><ShieldCheck /></span>
                <h3>Support Mindset</h3>
                <p>Calm troubleshooting, secure exam operations, step-by-step user guidance, deadline handling, independent work and team communication.</p>
                <div className="tags">
                  <span>User support</span><span>Process</span><span>Documentation</span>
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* TRAINING */}
        <section id="training" className="section" style={{ paddingTop: 0 }}>
          <div className="wrap">
            <div className="reveal">
              <span className="eyebrow">Training & Education</span>
              <h2 className="section-head">Formal training, practical reinforcement.</h2>
            </div>
            <div className="edu">
              <article className="edu-card reveal">
                <h3><span className="edu-ico"><BookOpen size={18} /></span>Professional Training</h3>
                <ul>
                  <li><strong>CompTIA A+</strong><span>New Horizons CLC</span></li>
                  <li><strong>CCNA</strong><span>New Horizons CLC</span></li>
                  <li><strong>Windows Server 2019 / MCSA</strong><span>New Horizons CLC</span></li>
                  <li><strong>MTCNA</strong><span>New Horizons CLC</span></li>
                </ul>
              </article>
              <article className="edu-card reveal">
                <h3><span className="edu-ico"><GraduationCap size={18} /></span>Academic Qualification</h3>
                <ul>
                  <li><strong>BSc in Computer Science & Engineering</strong><span>Dhaka International University · running</span></li>
                  <li><strong>Diploma in Computer Science & Technology</strong><span>ICST · Bangladesh Technical Board · GPA 3.71/4.00 · 2021</span></li>
                  <li><strong>Secondary School Certificate</strong><span>Business Studies · Cumilla Board · GPA 4.23/5.00 · 2017</span></li>
                </ul>
              </article>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="section" style={{ paddingTop: 0 }}>
          <div className="wrap">
            <div className="reveal">
              <span className="eyebrow">FAQ</span>
              <h2 className="section-head">Quick answers for hiring & search.</h2>
            </div>
            <FaqAccordion />
          </div>
        </section>

        {/* CONTACT */}
        <section id="contact" className="contact">
          <div className="wrap contact-grid">
            <div className="reveal">
              <span className="eyebrow">Contact</span>
              <h2 className="display">
                Let's <span className="it">work</span> together.
              </h2>
            </div>
            <div className="contact-channels reveal">
              <a href="mailto:md.rasel.mia10010@gmail.com">
                <Mail />
                <div>
                  <small>Email</small>
                  <strong>md.rasel.mia10010@gmail.com</strong>
                </div>
              </a>
              <a href="tel:+8801641108619">
                <Phone />
                <div>
                  <small>Phone</small>
                  <strong>+880 1641-108619</strong>
                </div>
              </a>
              <a href="https://wa.me/8801641108619" target="_blank" rel="noopener noreferrer">
                <MessageCircle />
                <div>
                  <small>WhatsApp</small>
                  <strong>Send a message</strong>
                </div>
              </a>
              <span>
                <MapPin />
                <div>
                  <small>Location</small>
                  <strong>Dhaka, Bangladesh</strong>
                </div>
              </span>
            </div>
            <div className="social-links reveal" aria-label="Social profiles">
              <a href="#" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" title="LinkedIn">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a href="#" target="_blank" rel="noopener noreferrer" aria-label="GitHub" title="GitHub">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>
              </a>
              <a href="#" target="_blank" rel="noopener noreferrer" aria-label="Facebook" title="Facebook">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="#" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" title="X (Twitter)">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="foot">
        <div className="wrap foot-inner">
          <span>© {new Date().getFullYear()} Md. Rasel Mia</span>
          <div className="foot-links">
            <a href="/about">About</a>
            <a href="/assets/Md-Rasel-Mia-CV.pdf" download>Download CV</a>
            <button type="button" className="foot-top-btn" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="Back to top">
              <ArrowUp size={16} />
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
